import { useQuery } from "@tanstack/react-query";
import { FileClock, Search } from "lucide-react";
import * as React from "react";

import { audit } from "@/api/endpoints";
import { useDebounced } from "@/hooks/useDebounced";
import { FilterBar, PageHeader, Pagination } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/utils";

/** KYC reads and money movements are the ones worth spotting at a glance. */
function toneForAction(action: string) {
  if (action.includes("kyc")) return "warning" as const;
  if (action.includes("wallet") || action.includes("refund")) return "critical" as const;
  if (action.includes("setting") || action.includes("staff")) return "serious" as const;
  return "default" as const;
}

export default function Audit() {
  const [page, setPage] = React.useState(1);
  const [action, setAction] = React.useState("");
  const [targetType, setTargetType] = React.useState("");

  const debouncedAction = useDebounced(action);
  const debouncedTarget = useDebounced(targetType);

  React.useEffect(() => setPage(1), [debouncedAction, debouncedTarget]);

  const query = useQuery({
    queryKey: ["audit", { page, debouncedAction, debouncedTarget }],
    queryFn: () =>
      audit.list({
        page,
        page_size: 25,
        action: debouncedAction || undefined,
        target_type: debouncedTarget || undefined,
      }),
  });

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Append-only record of every back-office action, including who viewed KYC data."
      />

      <FilterBar>
        <div className="relative min-w-[14rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="Filter by action, e.g. kyc"
            className="pl-8"
            aria-label="Filter by action"
          />
        </div>
        <div className="w-52">
          <Input
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
            placeholder="Target type, e.g. ticket"
            aria-label="Filter by target type"
          />
        </div>
      </FilterBar>

      <Card className="overflow-hidden">
        {query.isLoading ? (
          <TableSkeleton cols={5} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data && query.data.items.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.items.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="tabular whitespace-nowrap text-muted-foreground">
                      {formatDateTime(e.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={toneForAction(e.action)}>{e.action}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {e.target_type ? (
                        <>
                          <span>{e.target_type}</span>
                          {e.target_id && (
                            <span className="tabular block text-[11px] opacity-70">
                              {e.target_id}
                            </span>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="tabular text-xs text-muted-foreground">
                      {e.actor_user_id ?? "system"}
                    </TableCell>
                    <TableCell className="max-w-[22rem]">
                      {e.metadata ? (
                        <code className="block truncate text-[11px] text-muted-foreground">
                          {JSON.stringify(e.metadata)}
                        </code>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              page={query.data.page}
              pageSize={query.data.pageSize}
              total={query.data.total}
              onPageChange={setPage}
            />
          </>
        ) : (
          <EmptyState
            icon={FileClock}
            title="No audit events match"
            description="Try clearing the filters, or perform an action to generate one."
          />
        )}
      </Card>
    </>
  );
}
